import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Dimensions, NativeModules, Platform, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import AppRoutes from './app';

import AppLocale from '@common/configs/translation';
import { appLanguageSelector } from '@common/redux/selectors/language';
import { setLanguage } from '@common/redux/slice/language';
import {
  setDimensionStore,
  setLoadingDimension,
} from '@common/redux/slice/page';
import FCMComponent from '@common/utils/FCM/FCMComponent';
import { isEmpty } from 'lodash';
import { useLayoutEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';

type SearchQuery = {
  w?: string; // width
};
export function Routes() {
  const dispatch = useDispatch();
  const windowSize = Dimensions.get('window');
  const [customWidth, setCustomWidth] = useState<number | undefined>(undefined);

  const languageSetting = useSelector(appLanguageSelector);
  const currentAppLocale = AppLocale[languageSetting.locale] || AppLocale.ja;

  const onChange = useCallback(
    ({ window }: any) => {
      dispatch(setLoadingDimension(true));
      if (window) {
        dispatch(
          setDimensionStore({
            ...window,
            ...(customWidth ? { width: customWidth } : {}),
          })
        );
      }
    },
    [dispatch, customWidth]
  );

  const checkLocale = (systemLocale: string) =>
    systemLocale.includes('ja') ? 'ja' : 'en';

  useEffect(() => {
    if (windowSize) {
      dispatch(
        setDimensionStore({
          ...windowSize,
          ...(customWidth ? { width: customWidth } : {}),
        })
      );
    }
  }, [customWidth]);

  useEffect(() => {
    const event = Dimensions.addEventListener('change', onChange);
    return () => {
      event?.remove();
    };
  }, [onChange]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const query = queryString.parse(window.location.search) as SearchQuery;
      if (query.w) {
        setCustomWidth(parseInt(query.w));
      }
    }
  }, []);

  useLayoutEffect(() => {
    try {
      let locale = 'ja';

      if (Platform.OS === 'web') {
        const search = !isEmpty(window)
          ? queryString.parse(window?.location?.search)
          : {};

        if (!search.locale || typeof search.locale !== 'string') return;
        locale = search.locale;
      } else if (Platform.OS === 'ios') {
        const systemIosLocale = checkLocale(
          NativeModules.SettingsManager.settings.AppleLocale
        );

        locale = systemIosLocale;
      } else {
        locale = checkLocale(NativeModules.I18nManager.localeIdentifier);
      }

      dispatch(setLanguage(locale));
    } catch (error: any) {
      console.log('language error: ', error.message);
    }
  }, []);

  return (
    <IntlProvider
      locale={currentAppLocale?.locale || currentAppLocale}
      messages={currentAppLocale.messages}
    >
      <Suspense fallback={null}>
        <SafeAreaProvider>
          <FCMComponent>
            {customWidth ? (
              <View
                nativeID="routes"
                style={{
                  width: customWidth,
                  marginHorizontal: 'auto',
                  height: '100%',
                }}
              >
                <AppRoutes />
              </View>
            ) : (
              <AppRoutes />
            )}
          </FCMComponent>
        </SafeAreaProvider>
      </Suspense>
    </IntlProvider>
  );
}
